import { getStoreName, MIN_IN_MS, paramsToQuery } from '@/base/MaUtils.js'
import { cacheRequest, makeRequest } from '@/base/MaRequestPooler.js'
import { maLogger } from '@/base/logger'

/**
 * Returns simplified app info of given track ids
 * @param urlParams
 * - trackIds: comma separated trackIds
 * - store: store name string: play or ios if not given tries
 * - countryCode: optional two letter country code
 * @returns {Promise<*>}
 */
export function getSimpleAppInfo({ store, trackIds, countryCode }) {
  const _trackIds = trackIds || ''
  const trackIdsAr = typeof _trackIds === 'string' ? _trackIds.split(',') : _trackIds
  const _store = (store || trackIdsAr.length) && getStoreName(trackIdsAr[0])
  const APP_INFO_REQUEST_SIZE = 100
  if (!_store) {
    maLogger.warn('Simple app info missing store parameter')
  }

  const getRequest = (trackIdList) => {
    // playstore track ids are going over url size limits so using alternative method for them
    const trackIdsStr = trackIdList.join(',')
    if (trackIdsStr.length > 1500) {
      return makeRequest(`/app/info/simple/${_store}/${countryCode ? `country/${countryCode}` : ''}`, {
        method: 'POST',
        data: trackIdList,
      })
    }
    else if (trackIdsAr.length > 0) {
      return cacheRequest(`/app/info/simple/${_store}/${countryCode ? `country/${countryCode}` : ''
      }?trackIds=${trackIdsStr}`)
    }
    // if empty track id list return empty app info
    return Promise.resolve([])
  }

  // add store and store name to all responses
  const setStore = apps => (apps || [])
    .map((a) => {
      a.store = a.storeName = getStoreName(a.trackId)
      return a
    })

  if (trackIdsAr.length > APP_INFO_REQUEST_SIZE) {
    const requests = []
    for (let i = 0; i < trackIdsAr.length; i = i + APP_INFO_REQUEST_SIZE) {
      const trackIdsForReq = trackIdsAr.slice(i, (i + APP_INFO_REQUEST_SIZE))
      // divide trackIds to APP_INFO_REQUEST_SIZE per requests
      requests.push(getRequest(trackIdsForReq))
    }
    return Promise.all(requests)
      .then((responses) => {
        if (responses && responses.length) {
          return responses.reduce((res, data) => res.concat(data || []), [])
        }
        return []
      })
      .then(setStore)
  }
  return getRequest(trackIdsAr).then(setStore)
}

/**
 * Returns detailed app info of given track id and country
 * @param urlParams
 * - trackId: app trackId
 * - countryCode:
 * @returns {Promise<*>}
 */
export function getDetailedAppInfo({ trackId, countryCode, accountId }) {
  const query = paramsToQuery({ id: accountId })
  const request = cacheRequest(`/app/${trackId}/info/detailed/${countryCode || ''}?${query}`)

  const promise = request.then((data) => {
    data.developerName = data.developerName || data.developerId
    return data
  })

  promise.cancel = request.cancel

  return promise
}

export function appSearch({ store, query, countryCode }) {
  return makeRequest(`/app/search/store/${store}?${paramsToQuery({ query, country: countryCode })}`)
}

/**
 * Returns matching for given app to targetStore
 * @param trackIds
 * @param store main app store
 * @param clearCached force cache clearing
 * @returns {Promise<any>}
 */
export function getAppMatchMap({ trackIds, store }, clearCached = false) {
  const url = `/match/app/${store}?${paramsToQuery({ trackIds })}`
  return cacheRequest(url, {}, { cacheTimeout: 5 * MIN_IN_MS, clearCached })
}

/**
 * Makes an app match suggestion
 * @param store
 * @param iosTrackId
 * @param playTrackId
 * @returns {Promise<any>}
 */
export function suggestAppMatch({ store, iosTrackId, playTrackId }) {
  return makeRequest(`/match/app/suggest?${paramsToQuery({ store, iosTrackId, playTrackId })}`, {
    method: 'POST',
  })
}

/**
 * Returns app events
 * @param trackIds
 * @param store
 * @param countries
 * @param startDate
 * @param endDate
 * @returns {Promise<any>}
 */
export function getAppEventsBySelectedApps({ trackIds, store, countries, startDate, endDate }) {
  return makeRequest(`/in-app-events/${store}?${paramsToQuery({ countries, startDate, endDate })}`, {
    method: 'POST',
    data: trackIds,
  })
}
/**
 * Returns app events
 *
 * @param store - Ios/Play
 * @param countryCode - alpha2 country code
 * @param eventType - InAppEventType (LIVE, ENDED, AVAILABLE)
 * @param category - Ios/Play category id
 * @param startDate- ISO date string
 * @param endDate - ISO date string
 * @param offset - number of events to skip (default 0)
 * @param limit - number of events to be returned (default 200)
 * @returns {Promise<any>}
 */
export function getAppEventsBySelectedCategory({ store, countryCode, type, category, startDate, endDate, offset = 0, limit = 200 }) {
  const query = paramsToQuery({ type, category, startDate, endDate, offset, limit })
  return makeRequest(`/in-app-events/${store}/${countryCode}?${query}`)
}

/**
 * Return Selected Apps' Similar App List
 * @param trackId
 * @param startDate
 * @param countries
 * @param endDate
 * @returns {Promise<any>}
 */
export function getSimilarAppsOfSelectedApp({ trackId, countries, startDate, endDate }) {
  return makeRequest(`/similar-apps/${trackId}?${paramsToQuery({ countries, startDate, endDate })}`)
}

/**
 * Return App List Similar To Selected App
 * @param trackId
 * @param startDate
 * @param countries
 * @param endDate
 * @returns {Promise<any>}
 */
export function getAppsSimilarToSelectedApp({ trackId, countries, startDate, endDate }) {
  return makeRequest(`/similar-apps/referring/${trackId}?${paramsToQuery({ countries, startDate, endDate })}`)
}

/**
 * Return Country and Store Based Top Apps
 * @param storeName
 * @param countryCode
 * @param axiosCfg
 * @returns {Promise<any>}
 */
export function getOnboardingTopApps({ storeName, countryCode }, axiosCfg) {
  return makeRequest(`/app/onboarding/top-visible-apps/${storeName}/country/${countryCode}`, axiosCfg)
}

/**
 * Retrieves similar apps for a given trackId and optional parameters.
 *
 * @param {object} options - The options for the request.
 * @param {string} options.trackId - The ID of the app track.
 * @param {string|string[]} [options.countries] - One or multiple countries to filter by.
 * @param {string} [options.startDate] - The start date for the period.
 * @param {string} [options.endDate] - The end date for the period.
 *
 * @returns {Promise} The Promise object representing the HTTP request.
 */
export function getSimilarApps({ trackId, countries, startDate, endDate }) {
  return makeRequest(`/similar-apps/details/${trackId}?${paramsToQuery({ countries, startDate, endDate })}`)
}

/**
 * Retrieves PPO data for given trackIds and optional parameters.
 *
 * @param {object} options - The options for the request.
 * @param {string|string[]} options.trackIds - One or multiple app track IDs.
 * @param {string|string[]} options.countries - One or multiple countries to filter by.
 * @param {string} options.startDate - The start date for the period.
 * @param {string} options.endDate - The end date for the period.
 *
 * @returns {Promise} The Promise object representing the HTTP request.
 */
export function getPPO({ countries, trackIds, startDate, endDate }) {
  return makeRequest(`/appstore/ppo?startDate=${startDate}&endDate=${endDate}`, {
    method: 'POST',
    data: {
      trackIds,
      countries,
    },
  })
}

/**
 * Retrieves A/B Test data for given trackIds and optional parameters from the Play Store.
 *
 * @param {object} options - The options for the request.
 * @param {string|string[]} options.trackIds - One or multiple app track IDs.
 * @param {string|string[]} options.countries - One or multiple countries to filter by.
 * @param {string} options.startDate - The start date for the period.
 * @param {string} options.endDate - The end date for the period.
 *
 * @returns {Promise} The Promise object representing the HTTP request.
 */
export function getABTest({ countries, trackIds, startDate, endDate }) {
  return makeRequest(`/playstore/abtest?startDate=${startDate}&endDate=${endDate}`, {
    method: 'POST',
    data: {
      trackIds,
      countries,
    },
  })
}
